import type React from "react"

import {useRef, useState, useEffect} from "react"
import {Play, Pause, Volume2, VolumeX} from "lucide-react"
import {cn} from "@/lib/utils"

interface ModernAudioPlayerProps {
    src: string
    title?: string
    className?: string
    simple?: boolean
}

export function AudioPlayer({src, title = "אין שם", className, simple}: ModernAudioPlayerProps) {

    // Audio state
    const [isPlaying, setIsPlaying] = useState(false)
    const [duration, setDuration] = useState(0)
    const [currentTime, setCurrentTime] = useState(0)
    const [volume, setVolume] = useState(1)
    const [isMuted, setIsMuted] = useState(false)
    const [isDragging, setIsDragging] = useState(false)

    // Refs
    const audioRef = useRef<HTMLAudioElement>(null)
    const progressBarRef = useRef<HTMLDivElement>(null)
    const volumeBarRef = useRef<HTMLDivElement>(null)

    // Format time in MM:SS
    const formatTime = (time: number) => {
        if (isNaN(time)) return "0:00"

        const minutes = Math.floor(time / 60)
        const seconds = Math.floor(time % 60)
        return `${minutes}:${seconds.toString().padStart(2, "0")}`
    }

    // Handle play/pause
    const togglePlayPause = () => {
        const audio = audioRef.current
        if (!audio) return

        if (isPlaying) {
            audio.pause()
        } else {
            audio.play()
        }
        setIsPlaying(!isPlaying)
    }

    // Handle progress bar click
    const handleProgressClick = (e: React.MouseEvent<HTMLDivElement>) => {
        const progressBar = progressBarRef.current
        const audio = audioRef.current
        if (!progressBar || !audio) return

        const rect = progressBar.getBoundingClientRect()
        const clickPosition = (e.clientX - rect.left) / rect.width
        const newTime = clickPosition * duration

        audio.currentTime = newTime
        setCurrentTime(newTime)
    }

    // Handle progress bar drag
    const handleProgressDragStart = () => {
        setIsDragging(true)
    }

    const handleProgressDrag = (e: React.MouseEvent<HTMLDivElement>) => {
        if (!isDragging) return

        const progressBar = progressBarRef.current
        const audio = audioRef.current
        if (!progressBar || !audio) return

        const rect = progressBar.getBoundingClientRect()
        const clickPosition = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width))
        const newTime = clickPosition * duration

        audio.currentTime = newTime
        setCurrentTime(newTime)
    }

    const handleProgressDragEnd = () => {
        setIsDragging(false)
    }

    // Handle volume bar click
    const handleVolumeClick = (e: React.MouseEvent<HTMLDivElement>) => {
        const volumeBar = volumeBarRef.current
        const audio = audioRef.current
        if (!volumeBar || !audio) return

        const rect = volumeBar.getBoundingClientRect()
        const clickPosition = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width))

        audio.volume = clickPosition
        setVolume(clickPosition)

        if (clickPosition === 0) {
            setIsMuted(true)
        } else if (isMuted) {
            setIsMuted(false)
        }
    }

    // Toggle mute
    const toggleMute = () => {
        const audio = audioRef.current
        if (!audio) return

        if (isMuted) {
            audio.volume = volume
            audio.muted = false
        } else {
            audio.muted = true
        }

        setIsMuted(!isMuted)
    }

    // Update time as audio plays
    useEffect(() => {
        const audio = audioRef.current
        if (!audio) return

        const handleTimeUpdate = () => {
            if (!isDragging) {
                setCurrentTime(audio.currentTime)
            }
        }

        const handleLoadedMetadata = () => {
            setDuration(audio.duration)
        }

        const handleEnded = () => {
            setIsPlaying(false)
            setCurrentTime(0)
        }

        // Add event listeners
        audio.addEventListener("timeupdate", handleTimeUpdate)
        audio.addEventListener("loadedmetadata", handleLoadedMetadata)
        audio.addEventListener("ended", handleEnded)

        // Clean up
        return () => {
            audio.removeEventListener("timeupdate", handleTimeUpdate)
            audio.removeEventListener("loadedmetadata", handleLoadedMetadata)
            audio.removeEventListener("ended", handleEnded)
        }
    }, [isDragging])

    // Set up global mouse event listeners for dragging
    useEffect(() => {
        const handleMouseMove = (e: MouseEvent) => {
            if (isDragging) {
                const progressBar = progressBarRef.current
                const audio = audioRef.current
                if (!progressBar || !audio) return

                const rect = progressBar.getBoundingClientRect()
                const clickPosition = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width))
                const newTime = clickPosition * duration

                audio.currentTime = newTime
                setCurrentTime(newTime)
            }
        }

        const handleMouseUp = () => {
            if (isDragging) {
                setIsDragging(false)
            }
        }

        if (isDragging) {
            document.addEventListener("mousemove", handleMouseMove)
            document.addEventListener("mouseup", handleMouseUp)
        }

        return () => {
            document.removeEventListener("mousemove", handleMouseMove)
            document.removeEventListener("mouseup", handleMouseUp)
        }
    }, [isDragging, duration])

    return (
        <div className={cn("bg-white", className, !simple && 'p-1 border border-gray-300 w-full max-w-md')}
             aria-label="Audio player">
            <audio key={src} ref={audioRef} src={src} preload="metadata" onEnded={() => {
                setIsPlaying(false)
                setCurrentTime(0)
            }}/>

            <div className="flex items-center space-x-4">
                {/* Play/Pause Button */}
                <button
                    onClick={togglePlayPause}
                    className="transition-colors bg-white rounded-full p-2 cursor-pointer text-white hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-500"
                    aria-label={isPlaying ? "Pause" : "Play"}
                >
                    {isPlaying ? <Pause size={16} className="text-gray-400 "/> :
                        <Play size={16} className="text-gray-400"/>}
                </button>

                {!simple && (
                    <div className="flex-1 max-w-full min-w-1 space-y-1">
                        {/* Track Title */}
                        <div className="text-gray-600 text-xs leading-none max-w-40 truncate">{title}</div>

                        {/* Progress Bar */}
                        <div className="flex items-center gap-1">
                            <div
                                ref={progressBarRef}
                                className="relative h-1 bg-gray-600 rounded-full flex-1 cursor-pointer"
                                onClick={handleProgressClick}
                                onMouseDown={handleProgressDragStart}
                                onMouseMove={handleProgressDrag}
                                onMouseUp={handleProgressDragEnd}
                            >
                                <div
                                    className="absolute top-0 left-0 h-full bg-light-blue-500 rounded-full"
                                    style={{width: `${(currentTime / duration) * 100}%`}}
                                />
                                <div
                                    className="absolute top-1/2 -translate-y-1/2 h-3 w-3 bg-white rounded-full shadow-md"
                                    style={{
                                        left: `calc(${(currentTime / duration) * 100}% - 8px)`,
                                        display: currentTime > 0 ? "block" : "none",
                                    }}
                                />
                            </div>

                            {/* Time Display */}
                            <div className="text-gray-400 text-xs tabular-nums whitespace-nowrap leading-none">
                                {formatTime(currentTime)}/{formatTime(duration)}
                            </div>
                        </div>
                    </div>
                )}

                {!simple && (
                    /* Volume Control */
                    <div className="flex items-center space-x-2">
                        <button
                            onClick={toggleMute}
                            className="text-gray-500 hover:opacity-70 transition-opacity focus:outline-none"
                            aria-label={isMuted ? "Unmute" : "Mute"}
                        >
                            {isMuted ? <VolumeX size={20}/> : <Volume2 size={20}/>}
                        </button>

                        <div
                            ref={volumeBarRef}
                            className="relative w-16 h-1.5 bg-gray-600 rounded-full cursor-pointer hidden sm:block"
                            onClick={handleVolumeClick}
                        >
                            <div
                                className="absolute top-0 left-0 h-full bg-light-blue-500 rounded-full"
                                style={{width: `${isMuted ? 0 : volume * 100}%`}}
                            />
                        </div>
                    </div>

                )}
            </div>
        </div>
    )
}
